import { usePassportProvider } from "@/context";
import {
  Action,
  RewardsScreen,
  UserJourney,
  useRewardsAnalytics,
} from "@/context/AnalyticsProvider";
import { useGetWalletGems } from "@/hooks/api";
import { onDarkBase } from "@biom3/design-tokens";
import { Body } from "@biom3/react";
import DiamondIcon from "@mui/icons-material/Diamond";
import KeyIcon from "@mui/icons-material/Key";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { useRouter } from "next/navigation";
import { useWeeklyRewards } from "../WeeklyRewardsProvider";
import { OnboardingProgressBar } from "./OnboardingProgressBar";

const MAX_GEMS = 500;

export const HeaderOnboardingBar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { walletAddress } = usePassportProvider();
  const { walletGems } = useGetWalletGems(walletAddress || null, false);
  const { entryFee } = useWeeklyRewards();
  const router = useRouter();
  const { track } = useRewardsAnalytics();

  const currentGems = walletGems?.gems || 0;
  const totalGems = entryFee ?? MAX_GEMS;
  const progressPercentage = Math.min(100, (currentGems / totalGems) * 100);
  const gemsNeeded = Math.max(0, totalGems - currentGems);

  const getProgressBarColorAtPosition = (percentage: number) => {
    if (percentage <= 25) {
      return percentage < 12.5 ? "#87CEEB" : "#5DADE2";
    }
    if (percentage <= 50) {
      return percentage < 37.5 ? "#5DADE2" : "#4A9EDD";
    }
    if (percentage <= 75) {
      return percentage < 62.5 ? "#4A9EDD" : "#3A8DD4";
    }
    return percentage < 87.5 ? "#3A8DD4" : "#4A90E2";
  };

  const redirectToRewards = () => {
    track({
      action: Action.PRESSED,
      extras: {
        screen: RewardsScreen.ONBOARDING_PROGRESS_BAR,
        userJourney: UserJourney.ONBOARDING,
        control: "OnboardingProgressBar",
        controlType: "Button",
        progressBarType: "onboarding",
        currentGems,
        progressPercentage,
        gemsNeeded,
        targetUrl: "/rewards",
        onboardingComplete: currentGems >= totalGems,
      },
    });

    router.push("/rewards");
  };

  return (
    <Box
      width="100%"
      maxWidth="1200px"
      display="flex"
      flexDirection="row"
      alignItems="center"
      justifyContent="center"
      height={isMobile ? 60 : 78}
      position="relative"
      onClick={redirectToRewards}
      sx={{ cursor: "pointer" }}
    >
      {/* Container for Icons + Progress Bar */}
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        height={isMobile ? 30 : 40}
        gap={onDarkBase.spacing.x1}
        sx={{
          backgroundColor: onDarkBase.color.neutral[500],
          borderRadius: "250px",
          px: isMobile ? onDarkBase.spacing.x3 : onDarkBase.spacing.x2,
          width: "100%",
        }}
      >
        {/* Progress Bar Container */}
        <Box
          position="relative"
          sx={{
            cursor: "pointer",
            borderRadius: "18px",
            border: `${isMobile ? "6px" : "14px"} solid ${onDarkBase.color.neutral[500]}`,
            zIndex: 1,
            flex: 1,
            minWidth: 0,
          }}
        >
          <Box sx={{ position: "relative", zIndex: 2 }}>
            <OnboardingProgressBar height={isMobile ? "8px" : "12px"} />
          </Box>

          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: `${progressPercentage}%`,
              transform: "translate(-50%, -50%)",
              pointerEvents: "auto",
              zIndex: 4,
              cursor: "pointer",
            }}
            onClick={redirectToRewards}
          >
            {currentGems >= MAX_GEMS ? (
              <Box
                sx={{
                  width: isMobile ? "24px" : "36px",
                  height: isMobile ? "24px" : "36px",
                  borderRadius: "50%",
                  backgroundColor: onDarkBase.color.neutral[600],
                  border: `2px solid ${getProgressBarColorAtPosition(100)}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  animation: "gemKeyGlow 2s ease-in-out infinite",
                  "@keyframes gemKeyGlow": {
                    "0%, 100%": {
                      boxShadow: `0 0 8px ${getProgressBarColorAtPosition(100)}, 0 0 16px ${getProgressBarColorAtPosition(100)}, 0 0 24px ${getProgressBarColorAtPosition(100)}`,
                      transform: "scale(1)",
                    },
                    "50%": {
                      boxShadow: `0 0 12px ${getProgressBarColorAtPosition(100)}, 0 0 24px ${getProgressBarColorAtPosition(100)}, 0 0 36px ${getProgressBarColorAtPosition(100)}`,
                      transform: "scale(1.1)",
                    },
                  },
                }}
              >
                <KeyIcon
                  sx={{
                    fontSize: isMobile ? "12px" : "18px",
                    color: getProgressBarColorAtPosition(100),
                    transform: "rotate(90deg)",
                  }}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: isMobile ? "1px" : "4px",
                  px: isMobile ? onDarkBase.spacing.x1 : onDarkBase.spacing.x2,
                  py: isMobile ? 0 : onDarkBase.spacing.x1,
                  backgroundColor: onDarkBase.color.neutral[600],
                  borderRadius: onDarkBase.borderRadius.x4,
                  border: `2px solid ${getProgressBarColorAtPosition(progressPercentage)}`,
                  whiteSpace: "nowrap",
                }}
              >
                <DiamondIcon
                  sx={{
                    fontSize: isMobile ? "10px" : "16px",
                    color: onDarkBase.color.text.body.primary,
                  }}
                />
                <Body
                  size={isMobile ? "xxSmall" : "xSmall"}
                  sx={{
                    fontWeight: "bold",
                    color: onDarkBase.color.text.body.primary,
                    fontSize: isMobile ? "10px" : "12px",
                  }}
                >
                  {currentGems}
                </Body>
              </Box>
            )}
          </Box>

          {/* Top Indicator Container */}
          <Box
            sx={{
              position: "absolute",
              top: -5,
              left: "50%",
              transform: isMobile
                ? "translate(-50%, -60%)"
                : "translate(-50%, -78%)",
              px: onDarkBase.spacing.x4,
              py: onDarkBase.spacing.x2,
              backgroundColor: onDarkBase.color.neutral[500],
              clipPath:
                "polygon(8px 0%, calc(100% - 8px) 0%, 100% 50%, calc(100% - 8px) 100%, 8px 100%, 0% 50%)",
              whiteSpace: "nowrap",
              cursor: "pointer",
            }}
            onClick={redirectToRewards}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: onDarkBase.spacing.x2,
                position: "relative",
                top: "-6px",
              }}
            >
              <KeyIcon
                sx={{
                  fontSize: isMobile ? "12px" : "14px",
                  color: onDarkBase.color.text.body.primary,
                }}
              />
              <Body
                size={isMobile ? "xxSmall" : "xSmall"}
                sx={{
                  color: onDarkBase.color.text.body.primary,
                  fontWeight: "bold",
                }}
              >
                BEGIN YOUR JOURNEY GET YOUR KEY TODAY!
              </Body>
            </Box>
          </Box>

          {/* Bottom Indicator Container */}
          <Box
            sx={{
              position: "absolute",
              bottom: -5,
              left: "50%",
              transform: isMobile
                ? "translate(-50%, 60%)"
                : "translate(-50%, 79%)",
              px: onDarkBase.spacing.x6,
              py: onDarkBase.spacing.x2,
              backgroundColor: onDarkBase.color.neutral[500],
              clipPath:
                "polygon(8px 0%, calc(100% - 8px) 0%, 100% 50%, calc(100% - 8px) 100%, 8px 100%, 0% 50%)",
              whiteSpace: "nowrap",
              cursor: "pointer",
            }}
            onClick={redirectToRewards}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: onDarkBase.spacing.x1,
                position: "relative",
                top: "6px",
              }}
            >
              {gemsNeeded > 0 ? (
                <>
                  <Body
                    size={isMobile ? "xxSmall" : "xSmall"}
                    sx={{
                      color: onDarkBase.color.text.body.primary,
                      fontWeight: "bold",
                    }}
                  >
                    {gemsNeeded}
                  </Body>
                  <DiamondIcon
                    sx={{
                      fontSize: isMobile ? "12px" : "14px",
                      color: onDarkBase.color.text.body.primary,
                    }}
                  />
                  <Body
                    size={isMobile ? "xxSmall" : "xSmall"}
                    sx={{
                      color: onDarkBase.color.text.body.primary,
                      fontWeight: "bold",
                    }}
                  >
                    TO UNLOCK REWARDS!
                  </Body>
                </>
              ) : (
                <Body
                  size={isMobile ? "xxSmall" : "xSmall"}
                  sx={{
                    color: onDarkBase.color.text.body.primary,
                    fontWeight: "bold",
                  }}
                >
                  FINISH STEPS TO UNLOCK REWARDS!
                </Body>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
